import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { Box } from '@mui/material';

import ClientInvoicesView from './Components/ClientInvoicesView/ClientInvoicesView';
import MyAliroBillingView from './Components/MyAliroBillingView/MyAliroBillingView';
import TabNavigation, { type FinancialsTab } from './Components/TabNavigation/TabNavigation';
import ServiceProviderBilling from 'views/v2/serviceProviderBilling/containers';

import { Role } from 'data/constants/roles';

export default function Financials() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');

    // Get user to check if law firm (from /api/me response)
    const user = useSelector((state: RootState) => state.user.entity);
    const isLawFirm = user?.is_law_firm ?? false;
    const isSuperSuperAdmin = user?.role === Role.SUPER_SUPER_ADMIN;

    const [activeTab, setActiveTab] = useState<FinancialsTab>(() => {
        // Only allow my-billing tab if law firm
        if (tabParam === 'my-billing' && isLawFirm) {
            return 'my-billing';
        }
        return 'client-invoices';
    });

    // Update tab when URL parameter changes
    useEffect(() => {
        if (tabParam === 'my-billing') {
            // Only allow switching to my-billing if law firm
            if (isLawFirm) {
                setActiveTab('my-billing');
            }
            // Clean up the tab parameter after switching
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('tab');
            setSearchParams(newParams, { replace: true });
        }
    }, [tabParam, searchParams, setSearchParams, isLawFirm]);

    return (
        <Box sx={{ width: '100%' }}>
            <TabNavigation
                activeTab={activeTab}
                onTabChange={setActiveTab}
                isLawFirm={isLawFirm}
                isSuperSuperAdmin={isSuperSuperAdmin}
            />

            {activeTab === 'client-invoices' && <ClientInvoicesView />}
            {activeTab === 'my-billing' && isLawFirm && <MyAliroBillingView />}
            {activeTab === 'service-provider-billing' && isSuperSuperAdmin && (
                <ServiceProviderBilling />
            )}
        </Box>
    );
}
